import { db, type SiteRow, type UnitRow } from '../db';
import { SiteComponent, Site } from '../schemas';
import type { z } from 'zod';
import { newId } from '../../utils/ids';
import { updateSiteInspectionWindows } from '../../utils/inspectionWindows';
import { formatAddressParts } from '@/utils/address';
import { normalizeNameKey } from './repoUtils';
import { AbstractBaseRepo } from './baseRepo';
import { CompaniesRepo } from './companiesRepo';
import { ActionsRepo } from './actionsRepo';
import { softDeleteSyncMetadata, updateSyncMetadata } from '../sync/syncMetadata';
import { queueForSync } from '../sync/syncOutbox';
import { syncGuPublicationAction } from '../actions/guPublicationActions';
import {
  resolveLegacyDateToDateTime,
  resolvePlannedVisitAt,
  toIsoDateFromDateTime,
} from '@/utils/inspectionScheduling';
import { trackEntityCreated, trackEntityUpdated, trackEntityDeleted } from '@/lib/metrics';

class DuplicateSiteNameError extends Error {
  readonly field = 'name' as const;
  constructor(name: string) {
    super(`Esiste già uno stabilimento chiamato "${name}"`);
    this.name = 'DuplicateSiteNameError';
  }
}

type SiteComponentSchemaInput = z.input<typeof SiteComponent>;
type SiteComponentInput = Omit<SiteComponentSchemaInput, 'id' | 'quantity'> & {
  id?: string;
  quantity: number;
};

type SiteSchemaInput = z.input<typeof Site>;
export type SiteInput = Partial<
  Omit<
    SiteSchemaInput,
    | 'id'
    | 'companyId'
    | 'name'
    | 'sequenceNumber'
    | 'createdAt'
    | 'updatedAt'
    | 'siteComponents'
    | 'refrigerationUnitsCount'
  >
> & {
  id?: SiteSchemaInput['id'];
  companyId: SiteSchemaInput['companyId'];
  name: SiteSchemaInput['name'];
  sequenceNumber?: SiteSchemaInput['sequenceNumber'];
  createdAt?: SiteSchemaInput['createdAt'];
  updatedAt?: SiteSchemaInput['updatedAt'];
  siteComponents?: SiteComponentInput[];
};

export type SiteUpdateInput = Partial<Omit<SiteInput, 'id' | 'companyId'>>;

function isActiveUnit(unit: UnitRow): boolean {
  // Use syncDeletedAt as the single source of truth for deletion status
  return !unit.syncDeletedAt;
}

function toSite(row: SiteRow, units: UnitRow[]): Site {
  const activeUnits = units.filter(isActiveUnit);
  const periodicityNextVisit = row.periodicityNextVisit?.trim() || undefined;
  const periodicityNextVisitAt = resolvePlannedVisitAt({
    periodicityNextVisitAt: row.periodicityNextVisitAt,
    periodicityNextVisit,
  });
  return Site.parse({
    ...row,
    periodicityNextVisit,
    periodicityNextVisitAt,
    refrigerationUnits: activeUnits,
    refrigerationUnitsCount: activeUnits.length,
  });
}

class SitesRepoImpl extends AbstractBaseRepo<Site, Site, SiteRow, SiteInput> {
  protected readonly table = db.sites;

  protected getRowId(row: SiteRow): string {
    return row.id;
  }

  protected toListModel(row: SiteRow): Site {
    return toSite(row, []);
  }

  protected toGetModel(row: SiteRow): Site {
    return toSite(row, []);
  }

  protected getInputId(input: SiteInput): string | null {
    return input.id ?? null;
  }

  protected async toRow(
    input: SiteInput,
    ctx: { now: string; existing?: SiteRow }
  ): Promise<SiteRow> {
    const id = input.id ?? newId();
    const existing = ctx.existing;
    const optionalTrim = (value: string | undefined): string | undefined =>
      value?.trim() || undefined;
    const optionalTrimNullable = (value: string | null | undefined): string | undefined =>
      typeof value === 'string' ? value.trim() || undefined : undefined;
    const siblings = await this.table.where('companyId').equals(input.companyId).toArray();

    const trimmedName = input.name.trim();
    const nameKey = normalizeNameKey(trimmedName);
    const conflictingSibling = siblings.find(
      (site) => site.id !== id && normalizeNameKey(site.name) === nameKey
    );
    if (conflictingSibling) {
      throw new DuplicateSiteNameError(trimmedName);
    }

    const siblingSequences = siblings
      .filter((site) => site.id !== id)
      .map((site) => site.sequenceNumber ?? 0);
    const maxSequence = siblingSequences.length ? Math.max(...siblingSequences) : 0;
    const candidateSequence =
      typeof input.sequenceNumber === 'number' && input.sequenceNumber > 0
        ? input.sequenceNumber
        : maxSequence + 1;
    const sequenceNumber =
      existing?.sequenceNumber && existing.sequenceNumber > 0
        ? existing.sequenceNumber
        : candidateSequence;

    const sedeOperativaAddress =
      input.sedeOperativaAddress !== undefined
        ? optionalTrim(input.sedeOperativaAddress)
        : existing?.sedeOperativaAddress;
    const sedeOperativaCap =
      input.sedeOperativaCap !== undefined
        ? optionalTrim(input.sedeOperativaCap)
        : existing?.sedeOperativaCap;
    const sedeOperativaCity =
      input.sedeOperativaCity !== undefined
        ? optionalTrim(input.sedeOperativaCity)
        : existing?.sedeOperativaCity;
    const sedeOperativaProvince =
      input.sedeOperativaProvince !== undefined
        ? optionalTrim(input.sedeOperativaProvince)
        : existing?.sedeOperativaProvince;
    const composedSedeOperativa = formatAddressParts({
      address: sedeOperativaAddress,
      cap: sedeOperativaCap,
      city: sedeOperativaCity,
      province: sedeOperativaProvince,
    });

    const periodicity = input.periodicity !== undefined ? input.periodicity : existing?.periodicity;
    const contractActivationDate =
      input.contractActivationDate !== undefined
        ? optionalTrim(input.contractActivationDate)
        : existing?.contractActivationDate;

    const inspectionWindows = updateSiteInspectionWindows(contractActivationDate, periodicity);

    const siteComponents = (input.siteComponents ?? existing?.siteComponents ?? []).map(
      (component) => ({
        id: component.id ?? newId(),
        type: component.type.trim(),
        quantity: component.quantity,
        notes: optionalTrim(component.notes),
      })
    );

    const legacyNextVisit =
      input.periodicityNextVisit !== undefined
        ? optionalTrimNullable(input.periodicityNextVisit)
        : existing?.periodicityNextVisit;
    const explicitNextVisitAt =
      input.periodicityNextVisitAt !== undefined
        ? optionalTrimNullable(input.periodicityNextVisitAt)
        : existing?.periodicityNextVisitAt;
    const resolvedNextVisitAt =
      explicitNextVisitAt ?? resolveLegacyDateToDateTime(legacyNextVisit, '00:00');
    const derivedLegacyNextVisit = resolvedNextVisitAt
      ? toIsoDateFromDateTime(resolvedNextVisitAt)
      : legacyNextVisit;

    return {
      id,
      companyId: input.companyId,
      name: trimmedName,
      sequenceNumber,
      contactEmail:
        input.contactEmail !== undefined
          ? optionalTrim(input.contactEmail)
          : existing?.contactEmail,
      contactPhone:
        input.contactPhone !== undefined
          ? optionalTrim(input.contactPhone)
          : existing?.contactPhone,
      btGreaseBandPresent:
        input.btGreaseBandPresent !== undefined
          ? input.btGreaseBandPresent
          : existing?.btGreaseBandPresent,
      address: input.address !== undefined ? optionalTrim(input.address) : existing?.address,
      siteLocation:
        input.siteLocation !== undefined
          ? optionalTrim(input.siteLocation)
          : existing?.siteLocation,
      siteType: input.siteType !== undefined ? optionalTrim(input.siteType) : existing?.siteType,
      frequency:
        input.frequency !== undefined ? optionalTrim(input.frequency) : existing?.frequency,
      gasType: input.gasType !== undefined ? optionalTrim(input.gasType) : existing?.gasType,
      toxicGas: input.toxicGas !== undefined ? optionalTrim(input.toxicGas) : existing?.toxicGas,
      sedeOperativa:
        input.sedeOperativa !== undefined
          ? optionalTrim(input.sedeOperativa) || composedSedeOperativa
          : (existing?.sedeOperativa ?? composedSedeOperativa),
      sedeOperativaAddress,
      sedeOperativaCap,
      sedeOperativaCity,
      sedeOperativaProvince,
      periodicity,
      contractActivationDate,
      nextInspectionWindowStart: inspectionWindows.nextInspectionWindowStart,
      nextInspectionWindowEnd: inspectionWindows.nextInspectionWindowEnd,
      // CPI fields
      cpiPresent: input.cpiPresent !== undefined ? input.cpiPresent : existing?.cpiPresent,
      cpiProtocolNumber:
        input.cpiProtocolNumber !== undefined
          ? optionalTrim(input.cpiProtocolNumber)
          : existing?.cpiProtocolNumber,
      cpiLastRenewalDate:
        input.cpiLastRenewalDate !== undefined
          ? optionalTrim(input.cpiLastRenewalDate)
          : existing?.cpiLastRenewalDate,
      cpiExpiryDate:
        input.cpiExpiryDate !== undefined
          ? optionalTrim(input.cpiExpiryDate)
          : existing?.cpiExpiryDate,
      ammoniaActivityCodes:
        input.ammoniaActivityCodes !== undefined
          ? input.ammoniaActivityCodes
          : existing?.ammoniaActivityCodes,
      // Authorization fields
      authorizationProtocolNumber:
        input.authorizationProtocolNumber !== undefined
          ? optionalTrim(input.authorizationProtocolNumber)
          : existing?.authorizationProtocolNumber,
      authorizationIssueDate:
        input.authorizationIssueDate !== undefined
          ? optionalTrim(input.authorizationIssueDate)
          : existing?.authorizationIssueDate,
      authorizationExpiryDate:
        input.authorizationExpiryDate !== undefined
          ? optionalTrim(input.authorizationExpiryDate)
          : existing?.authorizationExpiryDate,
      authorizedNh3Kg:
        input.authorizedNh3Kg !== undefined ? input.authorizedNh3Kg : existing?.authorizedNh3Kg,
      officialGazettePublished:
        input.officialGazettePublished !== undefined
          ? input.officialGazettePublished
          : existing?.officialGazettePublished,
      officialGazetteDate:
        input.officialGazetteDate !== undefined
          ? optionalTrim(input.officialGazetteDate)
          : existing?.officialGazetteDate,
      periodicityControlPeriod:
        input.periodicityControlPeriod !== undefined
          ? optionalTrim(input.periodicityControlPeriod)
          : existing?.periodicityControlPeriod,
      periodicityPreviousVisit:
        input.periodicityPreviousVisit !== undefined
          ? optionalTrim(input.periodicityPreviousVisit)
          : existing?.periodicityPreviousVisit,
      periodicityNextVisit: derivedLegacyNextVisit,
      periodicityNextVisitAt: resolvedNextVisitAt,
      maintenanceNotes:
        input.maintenanceNotes !== undefined
          ? optionalTrim(input.maintenanceNotes)
          : existing?.maintenanceNotes,
      refrigerationUnitsCount: existing?.refrigerationUnitsCount ?? 0,
      siteComponents,
      notes: input.notes !== undefined ? optionalTrim(input.notes) : existing?.notes,
      createdAt: input.id ? input.createdAt || existing?.createdAt || ctx.now : ctx.now,
      updatedAt: ctx.now,
      // Sync metadata - will be overwritten by baseRepo.save()
      syncUpdatedAt: existing?.syncUpdatedAt ?? ctx.now,
      syncDeletedAt: existing?.syncDeletedAt,
      syncDeviceId: existing?.syncDeviceId,
      syncVersion: existing?.syncVersion ?? 0,
    };
  }

  async getSite(id: string): Promise<Site | null> {
    const row = await this.table.get(id);
    if (!row) return null;
    if (this.isDeleted(row)) return null;
    const units = await db.units.where('siteId').equals(id).toArray();
    return toSite(row, units);
  }

  override async list(): Promise<Site[]> {
    const [rows, units] = await Promise.all([this.table.toArray(), db.units.toArray()]);
    const activeRows = rows.filter((row) => !this.isDeleted(row));
    const unitsBySite = new Map<string, UnitRow[]>();
    units.filter(isActiveUnit).forEach((unit) => {
      const list = unitsBySite.get(unit.siteId);
      if (list) {
        list.push(unit);
      } else {
        unitsBySite.set(unit.siteId, [unit]);
      }
    });
    return activeRows.map((row) => toSite(row, unitsBySite.get(row.id) ?? []));
  }

  async saveSite(input: SiteInput): Promise<string> {
    const isUpdate = input.id ? await this.table.get(input.id) : null;
    const id = await db.transaction('rw', db.sites, async () => this.save(input));
    if (isUpdate) {
      trackEntityUpdated('site');
    } else {
      trackEntityCreated('site');
    }
    const site = await this.get(id);
    await syncGuPublicationAction({
      companyId: input.companyId,
      site,
    });
    return id;
  }

  async updateSite(id: string, input: Partial<Omit<SiteInput, 'id' | 'companyId'>>): Promise<void> {
    const existing = await this.table.get(id);
    if (!existing) {
      throw new Error(`Site with id ${id} not found`);
    }
    if (this.isDeleted(existing)) {
      throw new Error('Stabilimento archiviato');
    }

    const now = new Date().toISOString();
    const optionalTrimNullable = (value: string | null | undefined): string | undefined =>
      typeof value === 'string' ? value.trim() || undefined : undefined;

    const updates: Partial<SiteRow> = {
      updatedAt: now,
    };

    if (input.name !== undefined) {
      const trimmedName = input.name.trim();
      const siblings = await db.sites.where('companyId').equals(existing.companyId).toArray();
      const nameKey = normalizeNameKey(trimmedName);
      const conflict = siblings.find(
        (site) => site.id !== id && normalizeNameKey(site.name) === nameKey
      );
      if (conflict) {
        throw new DuplicateSiteNameError(trimmedName);
      }
      updates.name = trimmedName;
    }

    if (input.sedeOperativaAddress !== undefined)
      updates.sedeOperativaAddress = input.sedeOperativaAddress?.trim() || undefined;
    if (input.sedeOperativaCap !== undefined)
      updates.sedeOperativaCap = input.sedeOperativaCap?.trim() || undefined;
    if (input.sedeOperativaCity !== undefined)
      updates.sedeOperativaCity = input.sedeOperativaCity?.trim() || undefined;
    if (input.sedeOperativaProvince !== undefined)
      updates.sedeOperativaProvince = input.sedeOperativaProvince?.trim() || undefined;
    if (input.sedeOperativa !== undefined)
      updates.sedeOperativa = input.sedeOperativa?.trim() || undefined;

    if (
      input.sedeOperativaAddress !== undefined ||
      input.sedeOperativaCap !== undefined ||
      input.sedeOperativaCity !== undefined ||
      input.sedeOperativaProvince !== undefined
    ) {
      const composed = formatAddressParts({
        address:
          input.sedeOperativaAddress !== undefined
            ? input.sedeOperativaAddress
            : existing.sedeOperativaAddress,
        cap:
          input.sedeOperativaCap !== undefined ? input.sedeOperativaCap : existing.sedeOperativaCap,
        city:
          input.sedeOperativaCity !== undefined
            ? input.sedeOperativaCity
            : existing.sedeOperativaCity,
        province:
          input.sedeOperativaProvince !== undefined
            ? input.sedeOperativaProvince
            : existing.sedeOperativaProvince,
      });
      if (composed) {
        updates.sedeOperativa = composed;
      }
    }

    const needsWindowRecomputation =
      input.periodicity !== undefined || input.contractActivationDate !== undefined;

    if (input.periodicity !== undefined) updates.periodicity = input.periodicity;
    if (input.contractActivationDate !== undefined)
      updates.contractActivationDate = input.contractActivationDate?.trim() || undefined;

    if (needsWindowRecomputation) {
      const newContractActivationDate =
        input.contractActivationDate?.trim() || existing.contractActivationDate;
      const newPeriodicity = input.periodicity || existing.periodicity;

      const inspectionWindows = updateSiteInspectionWindows(
        newContractActivationDate,
        newPeriodicity
      );

      updates.nextInspectionWindowStart = inspectionWindows.nextInspectionWindowStart;
      updates.nextInspectionWindowEnd = inspectionWindows.nextInspectionWindowEnd;
    }

    if (input.nextInspectionWindowStart !== undefined)
      updates.nextInspectionWindowStart = input.nextInspectionWindowStart?.trim() || undefined;
    if (input.nextInspectionWindowEnd !== undefined)
      updates.nextInspectionWindowEnd = input.nextInspectionWindowEnd?.trim() || undefined;
    if (input.siteComponents !== undefined) {
      updates.siteComponents = input.siteComponents.map((component) => ({
        id: component.id ?? newId(),
        type: component.type.trim(),
        quantity: component.quantity,
        notes: component.notes?.trim() || undefined,
      }));
    }
    if (input.notes !== undefined) updates.notes = input.notes?.trim() || undefined;

    if (input.contactEmail !== undefined)
      updates.contactEmail = input.contactEmail?.trim() || undefined;
    if (input.contactPhone !== undefined)
      updates.contactPhone = input.contactPhone?.trim() || undefined;
    if (input.btGreaseBandPresent !== undefined)
      updates.btGreaseBandPresent = input.btGreaseBandPresent;
    if (input.address !== undefined) updates.address = input.address?.trim() || undefined;
    if (input.siteLocation !== undefined)
      updates.siteLocation = input.siteLocation?.trim() || undefined;
    if (input.siteType !== undefined) updates.siteType = input.siteType?.trim() || undefined;
    if (input.frequency !== undefined) updates.frequency = input.frequency?.trim() || undefined;
    if (input.gasType !== undefined) updates.gasType = input.gasType?.trim() || undefined;
    if (input.toxicGas !== undefined) updates.toxicGas = input.toxicGas?.trim() || undefined;
    if (input.periodicityControlPeriod !== undefined)
      updates.periodicityControlPeriod = input.periodicityControlPeriod?.trim() || undefined;
    if (input.periodicityPreviousVisit !== undefined)
      updates.periodicityPreviousVisit = input.periodicityPreviousVisit?.trim() || undefined;
    const hasNextVisitAtInput = input.periodicityNextVisitAt !== undefined;
    const hasNextVisitInput = input.periodicityNextVisit !== undefined;
    if (hasNextVisitAtInput || hasNextVisitInput) {
      const nextVisitAt = hasNextVisitAtInput
        ? optionalTrimNullable(input.periodicityNextVisitAt)
        : existing.periodicityNextVisitAt;
      const legacyNextVisit = hasNextVisitInput
        ? optionalTrimNullable(input.periodicityNextVisit)
        : existing.periodicityNextVisit;
      const resolvedNextVisitAt =
        nextVisitAt ?? resolveLegacyDateToDateTime(legacyNextVisit, '00:00');
      updates.periodicityNextVisitAt = resolvedNextVisitAt;
      updates.periodicityNextVisit = resolvedNextVisitAt
        ? toIsoDateFromDateTime(resolvedNextVisitAt)
        : legacyNextVisit;
    }
    if (
      !hasNextVisitAtInput &&
      !hasNextVisitInput &&
      !existing.periodicityNextVisitAt &&
      existing.periodicityNextVisit
    ) {
      const resolvedNextVisitAt = resolveLegacyDateToDateTime(
        existing.periodicityNextVisit,
        '00:00'
      );
      if (resolvedNextVisitAt) {
        updates.periodicityNextVisitAt = resolvedNextVisitAt;
      }
    }
    if (input.maintenanceNotes !== undefined)
      updates.maintenanceNotes = input.maintenanceNotes?.trim() || undefined;

    if (input.cpiPresent !== undefined) updates.cpiPresent = input.cpiPresent;
    if (input.cpiProtocolNumber !== undefined)
      updates.cpiProtocolNumber = input.cpiProtocolNumber?.trim() || undefined;
    if (input.cpiLastRenewalDate !== undefined)
      updates.cpiLastRenewalDate = input.cpiLastRenewalDate?.trim() || undefined;
    if (input.cpiExpiryDate !== undefined)
      updates.cpiExpiryDate = input.cpiExpiryDate?.trim() || undefined;
    if (input.ammoniaActivityCodes !== undefined)
      updates.ammoniaActivityCodes = input.ammoniaActivityCodes;

    if (input.authorizationProtocolNumber !== undefined)
      updates.authorizationProtocolNumber = input.authorizationProtocolNumber?.trim() || undefined;
    if (input.authorizationIssueDate !== undefined)
      updates.authorizationIssueDate = input.authorizationIssueDate?.trim() || undefined;
    if (input.authorizationExpiryDate !== undefined)
      updates.authorizationExpiryDate = input.authorizationExpiryDate?.trim() || undefined;
    if (input.authorizedNh3Kg !== undefined) updates.authorizedNh3Kg = input.authorizedNh3Kg;
    if (input.officialGazettePublished !== undefined)
      updates.officialGazettePublished = input.officialGazettePublished;
    if (input.officialGazetteDate !== undefined)
      updates.officialGazetteDate = input.officialGazetteDate?.trim() || undefined;

    // Add sync metadata for partial updates
    const syncMeta = updateSyncMetadata(existing);
    Object.assign(updates, syncMeta);

    await this.table.update(id, updates);
    trackEntityUpdated('site');

    const mergedSite = toSite({ ...existing, ...updates } as SiteRow, []);
    await syncGuPublicationAction({
      companyId: existing.companyId,
      site: mergedSite,
    });
  }

  async deleteSite(id: string): Promise<void> {
    await this.softDelete(id);
    trackEntityDeleted('site');

    // Cascade to refrigeration units
    const units = await db.units.where('siteId').equals(id).toArray();
    await Promise.all(
      units
        .filter((unit) => !unit.syncDeletedAt)
        .map(async (unit) => {
          const syncMeta = softDeleteSyncMetadata(unit);
          await db.units.update(unit.id, {
            ...syncMeta,
            updatedAt: syncMeta.syncUpdatedAt,
          });
          await queueForSync(db.units.name, unit.id, 'delete', { ...unit, ...syncMeta });
        })
    );

    // Cascade to actions
    const actions = await db.actions.where('siteId').equals(id).toArray();
    await Promise.all(actions.map((action) => ActionsRepo.softDelete(action.id)));

    const active = await CompaniesRepo.getActiveContext();
    if (active.siteId === id) {
      await CompaniesRepo.setActiveContext({
        companyId: active.companyId,
        siteId: null,
      });
    }
  }
}

export const SitesRepo = new SitesRepoImpl();
